/**
 * Pill styling shared by every top-level header item. `solid` is false while
 * the header floats over a dark hero, where the primary red would lose contrast.
 *
 * ## Why the pills are this size
 *
 * The desktop nav switches on at `lg` (1,200px), and at that width the row has
 * to hold the logo, seven top-level items and a right-hand cluster that now
 * carries a quotation-list badge, a theme toggle and two calls to action. Laid
 * out at the sizes this file used to publish — 15px labels in 16px of padding —
 * the row measured 1,214px against 1,152px of usable toolbar: 62px over, with
 * "Get a Quote" hanging 38px past the right edge of a 1,200px screen. The bar
 * is `position: fixed`, so that never surfaced as a page scrollbar; the button
 * was simply not there, which is the worst way for a primary CTA to fail.
 *
 * 14px labels in 12px of padding bring the row to 1,133px and give it 19px of
 * air at 1,200px. 14px is also what the nav of every comparable site settles
 * on: it is the size at which a seven-item row stops being a typographic
 * feature and starts being chrome, which is what it should have been reading
 * as all along.
 *
 * These numbers do not have to be exactly right, and that is the point of
 * shipping them alongside `useHeaderFit`. The hook measures the row that these
 * values produce and folds the tail of it away if it still does not fit, so a
 * longer label, a newly added menu or a visitor at 200% text size cannot put
 * the row back off the screen. What this file owns is the *comfortable* size;
 * the hook owns the guarantee.
 *
 * `xl` (1,536px) and up has the room, so the padding opens back up there. The
 * font size deliberately does not change with it: a nav whose labels grow a
 * pixel when a window crosses 1,536px reads as a layout shift rather than as
 * breathing room, and the padding alone carries the density difference.
 */

/**
 * The gap between pills, in theme spacing units (4px each).
 *
 * Published because two rows have to agree on it: the real nav and the hidden
 * measuring row that `useHeaderFit` reads its widths from. The hook itself
 * reads the *computed* gap off the DOM rather than importing this, so it stays
 * right even if a caller overrides it — this export exists so the two rows
 * cannot drift, not so the maths can be hard-coded.
 */
export const NAV_ROW_GAP = { lg: 0.5, xl: 1.5 } as const;

export function navItemSx(color: string, solid: boolean) {
  const accent = solid ? 'primary.main' : color;
  return {
    color,
    minHeight: 40,
    minWidth: 0,
    px: { lg: 3, xl: 5 },
    borderRadius: 999,
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none' as const,
    whiteSpace: 'nowrap' as const,
    '&:hover': { bgcolor: solid ? 'action.hover' : 'rgba(255,255,255,0.12)' },
    '&.active, &[data-active="true"]': {
      color: accent,
      bgcolor: solid ? 'action.selected' : 'rgba(255,255,255,0.16)',
    },
  };
}
